import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Heart } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const handleFaqClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      // If already on homepage, just scroll
      const faqElement = document.getElementById('faq');
      if (faqElement) {
        faqElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If on another page, navigate to homepage then scroll
      navigate('/');
      setTimeout(() => {
        const faqElement = document.getElementById('faq');
        if (faqElement) {
          faqElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-card/50 backdrop-blur-sm border-t border-border mt-auto" role="contentinfo">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-1">
              <span className="text-xl font-bold text-primary">mcqai</span>
              <span className="text-base text-muted-foreground">.online</span>
            </div>
            <p className="text-sm text-muted-foreground">
              AI-powered quiz generation for smarter learning
            </p>
          </div>

          {/* Quick Links */}
          <nav aria-label="Quick Links">
            <h2 className="font-semibold mb-4 text-sm uppercase tracking-wide">Quick Links</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  Dashboard
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  About Us
                </Link>
              </li>
            </ul>
          </nav>

          {/* Support */}
          <nav aria-label="Support">
            <h2 className="font-semibold mb-4 text-sm uppercase tracking-wide">Support</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  Contact Us
                </Link>
              </li>
              <li>
                <a 
                  href="/#faq" 
                  onClick={handleFaqClick}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer block"
                >
                  FAQ
                </a>
              </li>
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Legal">
            <h2 className="font-semibold mb-4 text-sm uppercase tracking-wide">Legal</h2>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a 
                  href="mailto:support@mcqai.online" 
                  className="text-sm text-muted-foreground hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
                  aria-label="Email support at support@mcqai.online"
                >
                  <Mail className="h-3 w-3" aria-hidden="true" />
                  support@mcqai.online
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <div className="border-t border-border mt-8 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              © {currentYear} MCQ AI. All rights reserved.
            </p>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              Made with <Heart className="h-4 w-4 text-destructive fill-destructive" aria-label="love" /> for learners worldwide
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
